import type { IBill, ISelectedItem, IUserItemSelection } from "@/data/model/Bill";

import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { trans } from "@/app/localization/Trans";
import { LineItem } from "@/app/ui/LineItem";
import { PaidLineItem } from "@/app/ui/PaidLineItem";
import { PullToRefresh } from "@/app/ui/PullToRefresh";

export class OrderSummary {
    private readonly $element: JQuery;
    private readonly $totalPaid: JQuery;
    private readonly $activeLines: JQuery;
    private readonly $paidLines: JQuery;
    public $scrollable: JQuery;
    public $clearBtn: JQuery;

    private lineItems: Array<LineItem>;
    private paidLineItems: Array<PaidLineItem>;

    public onModeChanged?: { (isActive: boolean) };

    constructor() {
        this.$element = $('<div class="ordersummary" />');
        this.$totalPaid = $('<div class="totalpaid" />').appendTo(this.$element);

        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$element);
        this.$activeLines = $('<div class="lines active" />').appendTo(this.$scrollable);
        this.$paidLines = $(
            '<div class="lines paid"><div class="section">' + trans.bill.fullyPaidItems + "</div></div>"
        )
            .hide()
            .appendTo(this.$scrollable);

        this.lineItems = new Array<LineItem>();
        this.paidLineItems = new Array<PaidLineItem>();

        this.$clearBtn = Ripple.bind(
            $('<div class="shown clearselect">' + trans.bill.clearSelection + "</div>").insertAfter(this.$totalPaid)
        ).click((e) => {
            e.preventDefault();
            if (this.isSelectionModeActive()) {
                this.clearItemSelection();
            } else {
                TastierClient.context.selectAll();
            }
        });

        new PullToRefresh(this.$scrollable, () => TastierClient.context.reloadBill());

        this.changeMode(this.isSelectionModeActive());
    }

    public reset() {
        this.lineItems = new Array<LineItem>();
        this.paidLineItems = new Array<PaidLineItem>();
        this.$totalPaid.empty();
        this.$totalPaid.removeClass("shown");
        this.$activeLines.empty();

        this.$paidLines.children(":not(.section)").remove();
        this.$paidLines.hide();

        this.changeMode(this.getCurrentSelection().length > 0);
    }

    public updateBill(bill?: IBill) {
        if (bill === undefined || bill.unpaidItems.length == 0) {
            this.$activeLines.html(
                '<div class="hint">' +
                    TastierClient.context.currentAllocation!.tableName +
                    ": " +
                    trans.bill.noOrderYet +
                    "</div>"
            );
            this.$clearBtn.hide();
            return;
        }

        this.$clearBtn.show();

        this.$activeLines.find("> .hint").remove();

        //check for items to remove
        for (const i in this.lineItems) {
            if (bill.unpaidItems.find((c) => c.id.toLowerCase() === i) === undefined) {
                const index = this.lineItems.indexOf(this.lineItems[i]);
                this.lineItems[i].remove();
                this.lineItems.splice(index, 1);
            }
        }

        for (const i in this.paidLineItems) {
            if (bill.paidItems.find((c) => c.id.toLowerCase() === i) === undefined) {
                const index = this.paidLineItems.indexOf(this.paidLineItems[i]);
                this.paidLineItems[i].remove();
                this.paidLineItems.splice(index, 1);
            }
        }

        //check for new items or updates
        for (const i in bill.unpaidItems) {
            const item = bill.unpaidItems[i];
            const selections = bill.selections.filter((c) => c.billItemId == item.id);

            const existing: LineItem = this.lineItems[item.id.toLowerCase()];
            if (existing !== undefined) {
                existing.setSelection(selections, false).update(item);

                if (existing.$element.parent().is(this.$paidLines)) {
                    existing.$element.detach();
                    existing.appendTo(this.$activeLines);
                }
            } else {
                const li = new LineItem().setSelection(selections, false).update(item);
                this.lineItems[item.id.toLowerCase()] = li;
                li.onSelect = () => {
                    this.changeItemSelection();
                };

                li.appendTo(this.$activeLines);
            }
        }

        for (const i in bill.paidItems) {
            const item = bill.paidItems[i];

            const existing: PaidLineItem = this.paidLineItems[item.id.toLowerCase()];
            if (existing !== undefined) {
                existing.update(item);

                //check if item was fully paid and needs to be moved
                if (existing.$element.parent().is(this.$activeLines)) {
                    existing.$element.detach();
                    existing.appendTo(this.$paidLines);
                }
            } else {
                const li = new PaidLineItem().update(item);
                this.paidLineItems[item.id.toLowerCase()] = li;

                li.appendTo(this.$paidLines);
            }
        }

        if (bill.paidItems.length > 0) {
            this.$paidLines.show();
        } else {
            this.$paidLines.hide();
        }

        //sort ascending
        this.$activeLines
            .children(".lineitem")
            ["sort"]((a, b) => {
                return $(a).data("sortOrder") - $(b).data("sortOrder");
            })
            .appendTo(this.$activeLines);

        this.$paidLines
            .children(".lineitem")
            ["sort"]((a, b) => {
                return $(a).data("sortOrder") - $(b).data("sortOrder");
            })
            .appendTo(this.$paidLines);

        //check if user has one selection
        this.changeMode(this.getCurrentSelection().length > 0);
        this.$element.addClass("selectionmode");

        this.setTotalPaid(bill.totalPaid === 0 ? undefined : bill.totalPaid);
    }

    private changeItemSelection() {
        const currentSelection = this.getCurrentSelection();
        TastierClient.context.selectItemsToPay(currentSelection);
    }

    private isSelectionModeActive(): boolean {
        return this.getCurrentSelection().length > 0;
    }

    private getCurrentSelection(): ISelectedItem[] {
        const currentSelection = new Array<ISelectedItem>();
        for (const i in this.lineItems) {
            const selection = this.lineItems[i].getSelection();
            if (selection === undefined) {
                continue;
            }

            currentSelection.push(selection);
        }

        return currentSelection;
    }

    private clearItemSelection() {
        for (const i in this.lineItems) this.lineItems[i].clearSelection();

        TastierClient.context.selectItemsToPay([]);
        this.changeMode(false);
    }

    public updateSelection(userItemSelections: Array<IUserItemSelection>) {
        for (const i in this.lineItems) {
            const item = this.lineItems[i];

            const selections = userItemSelections.filter(
                (c) => c.billItemId == item.model!.id && c.identityId === TastierClient.identityContext.getIdentityId()
            );
            item.setSelection(selections, true);
        }

        this.changeMode(this.getCurrentSelection().length > 0);
    }

    private changeMode(selectionModeActive: boolean) {
        if (selectionModeActive) {
            //check to add clear btn
            this.$clearBtn.text(trans.bill.clearSelection);
            this.$clearBtn.removeClass("selectall");
            this.$clearBtn.addClass("clearselect");
        } else {
            this.$clearBtn.text(trans.bill.selectAll);
            this.$clearBtn.removeClass("clearselect");
            this.$clearBtn.addClass("selectall");
        }

        for (const lineItem in this.lineItems) {
            this.lineItems[lineItem].updateSelectionMode(selectionModeActive);
        }

        if (this.onModeChanged !== undefined) {
            this.onModeChanged(selectionModeActive);
        }
    }

    private setTotalPaid(amt?: number) {
        this.$totalPaid.data("val", amt);
        if (amt === undefined) {
            this.$element.removeClass("haspayments");

            if (this.$totalPaid.removeClass("shown")) {
                this.$totalPaid.removeClass("shown");
                const existingInner = this.$totalPaid
                    .find("> .inner")
                    .removeClass("shown")
                    .delay(200)
                    .queue((e) => {
                        existingInner.remove();
                        e();
                    });
            }

            return;
        }

        const newInner = $(
            '<div class="inner">' +
                trans.bill.paid +
                ' <span class="value">CHF ' +
                amt.toFixed(2) +
                '</span><span class="excl">' +
                trans.bill.exclTip +
                "</span></div>"
        );
        if (this.$totalPaid.hasClass("shown")) {
            const oldInner = this.$totalPaid.find("> .inner");
            if (oldInner.length > 0) {
                oldInner.addClass("nodelay")[0].offsetHeight;
                oldInner
                    .addClass("gone")
                    .delay(200)
                    .queue((e) => {
                        oldInner.remove();
                        e();
                    });
            }

            newInner.appendTo(this.$totalPaid)[0].offsetHeight;
            newInner.addClass("shown");
        } else {
            this.$element.addClass("haspayments");
            newInner.appendTo(this.$totalPaid.empty());
            newInner[0].offsetHeight;
            this.$totalPaid.addClass("shown");
            newInner.addClass("shown");
        }
    }

    public appendTo($element: JQuery): OrderSummary {
        this.$element.appendTo($element);

        return this;
    }
}
